import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  timer,
} from 'rxjs';
import { filter, map, takeUntil, first } from 'rxjs/operators';
import { AuthUser } from '../models/auth.model';
import { jwtDecode } from 'jwt-decode';
import * as _ from 'lodash';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private authenticationContext: Subject<AuthUser | null> =
    new Subject<AuthUser | null>();
  public authenticationContext$ = this.authenticationContext.asObservable();
  private readonly _destroying$ = new Subject<void>();

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private isLoggedOutSubject$ = new BehaviorSubject<boolean>(false);
  private isLoggetOut$ = this.isLoggedOutSubject$.asObservable();

  public expiration: Date | null;

  public get IsTokenValid(): boolean {
    return (
      !_.isNil(this.getIdToken()) &&
      !_.isNil(this.expiration) &&
      this.expiration > new Date()
    );
  }

  public get IsTokenExpired(): boolean {
    return (
      !_.isNil(this.getIdToken()) &&
      !_.isNil(this.expiration) &&
      this.expiration <= new Date()
    );
  }

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    private msalService: MsalService
  ) {
    this._destroying$ = new Subject<void>();
    this.broadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.refreshAuthUser();
      });

    let refreshTime = moment(this.expiration).add(-10, 'minutes').toDate();
    timer(refreshTime)
      .pipe(first())
      .subscribe((_) => {
        if (this.IsTokenExpired) {
          this.forceRefresh();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  async forceRefresh() {
    var request = {
      scopes: ['user.read'],
      account: this.currentUser,
      forceRefresh: true,
      refreshTokenExpirationOffsetSeconds: 7200,
    };

    await this.msalService.instance
      .acquireTokenSilent(request)
      .catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          await this.msalService.instance.acquireTokenRedirect(request);
        }
      });

    this.refreshAuthUser();
  }

  canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));

  login() {
    this.isAuthenticatedSubject$.next(false);
    this.isDoneLoadingSubject$.next(false);
    this.isLoggedOutSubject$.next(false);

    if (this.currentUser == null)
      if (this.msalGuardConfig.authRequest) {
        this.msalService
          .loginRedirect({
            ...this.msalGuardConfig.authRequest,
          } as RedirectRequest)
          .subscribe(() => {
            this.refreshAuthUser();
          });
      } else {
        this.isAuthenticatedSubject$.next(true);
        this.isDoneLoadingSubject$.next(true);
        this.isLoggedOutSubject$.next(false);
        this.msalService.loginRedirect();
      }
  }

  logout() {
    this.isLoggedOutSubject$.next(true);
    return this.msalService.logoutRedirect();
  }

  get currentUser(): AccountInfo | null {
    if (this.msalService.instance.getAllAccounts().length > 0) {
      return this.msalService.instance.getAllAccounts()[0];
    }
    return null;
  }

  getIdToken(): string {
    if (!this.authenticated && !this.isLoggetOut$) {
      this.login();
      return '';
    }

    const allAccounts = this.msalService.instance.getAllAccounts();
    return allAccounts.length > 0 ? allAccounts[0].idToken : '';
  }

  get authenticated(): boolean {
    return this.msalService.instance.getActiveAccount() ? true : false;
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      return null;
    }
  }

  setExpirationDate() {
    const decodedToken = this.getDecodedAccessToken(this.getIdToken());
    this.expiration = new Date(decodedToken.exp * 1000);
  }

  private refreshAuthUser() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      activeAccount = accounts[0];
      this.msalService.instance.setActiveAccount(activeAccount);
    }
    if (activeAccount) {
      this.isAuthenticatedSubject$.next(true);
      this.isDoneLoadingSubject$.next(true);
      this.isLoggedOutSubject$.next(false);
      this.authenticationContext.next({
        name: activeAccount.name!,
        id: activeAccount.homeAccountId,
        email: activeAccount.username,
      });
      this.setExpirationDate();
    } else {
      this.expiration = null;
      this.isAuthenticatedSubject$.next(false);
      this.isDoneLoadingSubject$.next(false);
      this.authenticationContext.next(null);
    }
  }
}
